// Direct imports
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";

import { TraceBehavior } from "./tracebehavior";
import type { MeshBackgroundData } from "../scenedata/behaviors/meshbackgrounddata";
import { ValidationError } from "../scenedata/behaviordata";
import { Logger } from "../utils/Logger";

/**
 * The shape-channel morphs this behavior owns, by their authored names.
 * Matched case-insensitively and exactly — deliberately disjoint from the
 * `[XYZ][pn]?_(In|Out)` stretch convention MorphStretch owns, so the two
 * behaviors can share a rig without stepping on each other.
 */
const CORNER_RADIUS = "corner radius";
const OUTER_CORNER_RADIUS = "outer corner radius";
const STROKE = "stroke";

function isShapeChannel(name: string): boolean {
    const n = name.trim().toLowerCase();
    return n === CORNER_RADIUS || n === OUTER_CORNER_RADIUS || n === STROKE;
}

/**
 * MeshBackground — rounded rectangle / pill / circle from one authored mesh,
 * by driving its `Corner Radius` morph (ported from the AILayout prototype's
 * MeshBackgroundBehavior, thinned to the geometry channel).
 *
 * The math, verified against the library assets: the rig is authored on a
 * rest box whose `Corner Radius` target at influence 1 produces the fully
 * rounded shape (radius = half the smaller rest side), and the arc points
 * are affine in the radius, so a linear morph blend is a geometrically
 * EXACT rounded rect at every influence:
 *
 *     influence = 2 · radius / min(restWidth, restHeight)
 *
 * Radius semantics are CSS-like: measured on the rest box, so a stretched
 * background keeps its authored corner size — the stretch morphs translate
 * the corner arcs rigidly. The only place current size enters is the clamp:
 * radius never exceeds half the current smaller side (a background shrunk
 * below its rest size keeps a valid rounded rect instead of self-crossing).
 *
 * v1 scope, deliberately: no stroke (its inward growth needs a sizing
 * hand-off with MorphStretch — designed, not built), no fills/gradients/
 * media (Renderer's and Image's jobs). The `Outer Corner Radius` and
 * `Stroke` channels are pinned to 0 so assets that ship with non-zero
 * defaults load in a known state.
 *
 * Export note: like MorphStretch, influences are runtime mesh state — dumb
 * clients that load the raw GLB see the sharp rest shape. Baking is the
 * same known follow-up.
 */
export class MeshBackground extends TraceBehavior<MeshBackgroundData> {
    static readonly behaviorType = "MeshBackground" as const;

    private _meshes: AbstractMesh[] = [];
    /** mesh → object-root matrix per mesh, for measuring in one shared space. */
    private _rels = new Map<AbstractMesh, Matrix>();
    /** Smaller of the rest box's X/Y extents, meters — the calibration basis. */
    private _restMin = 0;
    private _warned = new Set<string>();
    /** Shape-channel influences at attach, per mesh (lowercased name → value). */
    private _preAttachInfluences = new Map<AbstractMesh, Map<string, number>>();

    protected validate(data: MeshBackgroundData): ValidationError[] {
        const errors: ValidationError[] = [];
        // TS can't guard wire data — a .trace file can carry anything here.
        const r = data.cornerRadius as unknown;
        if (r !== undefined && (typeof r !== "number" || !Number.isFinite(r) || r < 0)) {
            errors.push({ field: "cornerRadius", message: "cornerRadius must be a non-negative number (meters)", code: "INVALID_RADIUS" });
        }
        // Strict like `fit` one file over: `pill: "true"` would otherwise
        // coerce to false against the `=== true` apply-time test and silently
        // author a square-cornered panel.
        const pill = data.pill as unknown;
        if (pill !== undefined && typeof pill !== "boolean") {
            errors.push({ field: "pill", message: "pill must be a boolean", code: "INVALID_PILL" });
        }
        return errors;
    }

    // Meshes exist at attach time (load() resolves before attach — the same
    // contract MorphStretch, Bevel and Renderer rely on).
    protected onAttach(): void {
        if (this._resolveMeshes()) this._captureShapeInfluences();
        this._apply();
    }

    protected async applyCommit(): Promise<void> {
        this._apply();
    }

    /**
     * Re-clamp and re-apply the shape channels against the CURRENT extents.
     * MorphStretch calls this at the end of its own apply: the radius clamp
     * (and pill influence) are functions of the live box, so any resize after
     * this behavior's last apply — attach-order races, inspector size edits,
     * per-layout-pass setResolvedSize — would otherwise leave a stale clamp
     * (a shrink past the clamped radius self-crosses the corner geometry).
     */
    refreshShape(): void {
        if (!this.attachedNode) return;
        this._apply();
    }

    protected onDetach(): void {
        // Restore the pre-attach shape influences — not a blanket zero: an
        // asset can ship non-zero default influences (stroke, outer radius),
        // and add-then-remove must render like never-added. Only the shape
        // channels — stretch influences belong to MorphStretch and must
        // survive our detach.
        for (const mesh of this._meshes) {
            if (mesh.isDisposed()) continue;
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            const saved = this._preAttachInfluences.get(mesh);
            for (let i = 0; i < mgr.numTargets; i++) {
                const t = mgr.getTarget(i);
                if (t && isShapeChannel(t.name)) {
                    t.influence = saved?.get(t.name.trim().toLowerCase()) ?? 0;
                }
            }
        }
        this._meshes = [];
        this._rels.clear();
        this._restMin = 0;
        this._warned.clear();
        this._preAttachInfluences.clear();
    }

    /** Shape-channel influences as the asset shipped them, for detach restore. */
    private _captureShapeInfluences(): void {
        this._preAttachInfluences.clear();
        for (const mesh of this._meshes) {
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            const saved = new Map<string, number>();
            for (let i = 0; i < mgr.numTargets; i++) {
                const t = mgr.getTarget(i);
                if (t && isShapeChannel(t.name)) saved.set(t.name.trim().toLowerCase(), t.influence);
            }
            this._preAttachInfluences.set(mesh, saved);
        }
    }

    private _warnOnce(key: string, message: string): void {
        if (this._warned.has(key)) return;
        this._warned.add(key);
        Logger.warn(message);
    }

    private _apply(): void {
        if (!this._resolveMeshes()) return;
        if (!(this._restMin > 0)) return;

        const pill = this._resolved.pill === true;
        const radius = typeof this._resolved.cornerRadius === "number" && this._resolved.cornerRadius > 0
            ? this._resolved.cornerRadius
            : 0;

        // influence 1 ⟺ radius = restMin/2; the morphs reach no further, so
        // a larger request saturates (and says so once PER radius — a later,
        // different out-of-reach radius deserves its own warning).
        const requested = pill ? 1 : (2 * radius) / this._restMin;
        if (!pill && requested > 1) {
            this._warnOnce(`reach:${radius}`, `[MeshBackground] "${this.sceneObject.name}" cornerRadius ${radius} exceeds ` +
                `the morphs' reach (${(this._restMin / 2).toFixed(3)} m — half the rest box's smaller side); saturating`);
        }

        // Never round past half the CURRENT smaller side — a background
        // shrunk below rest (MorphStretch In-morphs) keeps a valid shape.
        const currentMin = this._measureMinXY(/* zeroShapeChannels */ true);
        // The pill contract ("radius = half the current smaller side") is
        // physically unreachable once the min axis is Out-stretched past rest:
        // the morphs top out at restMin/2. Say so, like the non-pill path does.
        if (pill && currentMin > this._restMin + 1e-6) {
            // Keyed on the CONDITION, not the measured value — a drag past
            // rest re-measures every pass and must not log once per mm.
            this._warnOnce(`pill:over-rest`, `[MeshBackground] "${this.sceneObject.name}" pill: ` +
                `smaller side ${currentMin.toFixed(3)} m exceeds rest ${this._restMin.toFixed(3)} m; ` +
                `corner radius tops out at ${(this._restMin / 2).toFixed(3)} m (morph reach), ends are rounded, not full semicircles`);
        }
        const byBounds = currentMin > 0
            ? Math.max(0, Math.min(1, currentMin / this._restMin))
            : 1;
        const inner = Math.min(1, requested, byBounds);

        for (const mesh of this._meshes) {
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            for (let i = 0; i < mgr.numTargets; i++) {
                const t = mgr.getTarget(i);
                if (!t) continue;
                const n = t.name.trim().toLowerCase();
                if (n === CORNER_RADIUS) t.influence = inner;
                // v1 pins the stroke channels: no stroke support yet, and
                // assets can ship with non-zero default influences.
                else if (n === OUTER_CORNER_RADIUS || n === STROKE) t.influence = 0;
            }
        }

        for (const mesh of this._meshes) {
            mesh.computeWorldMatrix(true);
            mesh.refreshBoundingInfo(false, true);
        }
    }

    /** Collect meshes, cache mesh→root matrices, measure the rest basis. */
    private _resolveMeshes(): boolean {
        if (this._meshes.length && this._meshes.every(m => !m.isDisposed())) return true;

        // Meshes this object OWNS — the walk stops at every child SceneObject,
        // the same line MorphStretch was narrowed to in #700 and the same one
        // `exportFilters.ownedExportNodes` draws for the bake. Deforming
        // through a child object shaped a mesh on screen that no bake plan
        // owns: the parent's plan stops at the child, and the child, having no
        // MeshBackground of its own, plans nothing — so it shipped at rest
        // while the editor showed it rounded. A child that should round gets
        // its own MeshBackground.
        const meshes = this.sceneObject.getOwnedMeshes().filter(m => m instanceof Mesh);
        if (!meshes.length) {
            this._warnOnce("nomesh", `[MeshBackground] "${this.sceneObject.name}" has no meshes`);
            return false;
        }
        const hasChannel = meshes.some(m => {
            const mgr = m.morphTargetManager;
            if (!mgr) return false;
            for (let i = 0; i < mgr.numTargets; i++) {
                const t = mgr.getTarget(i);
                if (t && t.name.trim().toLowerCase() === CORNER_RADIUS) return true;
            }
            return false;
        });
        if (!hasChannel) {
            this._warnOnce("norig", `[MeshBackground] "${this.sceneObject.name}" has no "Corner Radius" morph — ` +
                `this asset is not a rounded-background rig (use Bevel for plain primitives)`);
            return false;
        }

        this._meshes = meshes;
        const root = this.sceneObject.transform;
        root.computeWorldMatrix(true);
        const invRoot = root.getWorldMatrix().clone().invert();
        this._rels.clear();
        for (const mesh of meshes) {
            mesh.computeWorldMatrix(true);
            this._rels.set(mesh, mesh.getWorldMatrix().multiply(invRoot));
        }

        this._restMin = this._measureMinXY(/* zeroShapeChannels */ false, /* zeroAll */ true);
        return true;
    }

    /**
     * The smaller of the X/Y extents of the mesh union in object-root space,
     * meters. `zeroAll` measures the true rest shape (every morph at 0 —
     * the calibration basis); `zeroShapeChannels` zeroes only our own
     * channels, leaving MorphStretch's live stretch in place — the CURRENT
     * box the radius must fit. Influences are snapshotted and restored.
     */
    private _measureMinXY(zeroShapeChannels: boolean, zeroAll = false): number {
        const snapshot: [AbstractMesh, Map<string, number>][] = [];
        for (const mesh of this._meshes) {
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            const saved = new Map<string, number>();
            for (let i = 0; i < mgr.numTargets; i++) {
                const t = mgr.getTarget(i);
                if (!t) continue;
                if (zeroAll || (zeroShapeChannels && isShapeChannel(t.name))) {
                    saved.set(t.name, t.influence);
                    t.influence = 0;
                }
            }
            snapshot.push([mesh, saved]);
        }

        const min = new Vector3(Infinity, Infinity, Infinity);
        const max = new Vector3(-Infinity, -Infinity, -Infinity);
        const corner = new Vector3();
        for (const mesh of this._meshes) {
            mesh.refreshBoundingInfo(false, true);
            const rel = this._rels.get(mesh);
            if (!rel) continue;
            const bb = mesh.getBoundingInfo().boundingBox;
            for (const v of bb.vectors) {
                Vector3.TransformCoordinatesToRef(v, rel, corner);
                min.minimizeInPlace(corner);
                max.maximizeInPlace(corner);
            }
        }

        for (const [mesh, saved] of snapshot) {
            const mgr = mesh.morphTargetManager;
            if (!mgr) continue;
            for (const [name, value] of saved) {
                const t = mgr.getTargetByName(name);
                if (t) t.influence = value;
            }
        }

        if (!Number.isFinite(min.x)) return 0;
        return Math.min(max.x - min.x, max.y - min.y);
    }
}
